import { useEffect, useRef, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Loader2, ShoppingCart } from "lucide-react";
import TagsMenuBar from "@/components/TagsMenuBar";
import PublicHeader from "@/components/PublicHeader";
import { fetchTag } from "@/lib/api";

// Sandboxed iframe that renders the tag's pasted HTML faithfully and
// auto-resizes to fit its content (mirrors PageView's HtmlFrame).
function HtmlFrame({ html, title }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(500);
  const resize = () => {
    try {
      const doc = ref.current?.contentWindow?.document;
      if (!doc?.body) return;
      // Pasted HTML often uses full-viewport min-heights / body margins which
      // make the iframe far taller than its real content (huge gap before the
      // products grid). Neutralise them, then hug the actual content height.
      doc.documentElement.style.minHeight = "0";
      doc.body.style.minHeight = "0";
      doc.body.style.margin = "0";
      const h = doc.body.scrollHeight;
      if (h) setHeight(h);
    } catch {
      /* cross-origin guard */
    }
  };
  useEffect(() => {
    const id = setInterval(resize, 600);
    const stop = setTimeout(() => clearInterval(id), 4000);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
    };
  }, [html]);
  return (
    <iframe
      ref={ref}
      title={title || "tag"}
      srcDoc={html}
      onLoad={resize}
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      className="w-full border-0 block"
      style={{ height }}
      data-testid="tag-html-frame"
    />
  );
}

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function TagPage() {
  const { slug } = useParams();
  const dealer = new URLSearchParams(useLocation().search).get("d") || "orevital";
  const [tag, setTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    fetchTag(slug)
      .then((t) => {
        if (!alive) return;
        if (!t) {
          setError("This topic page was not found.");
        } else {
          setTag(t);
        }
      })
      .catch(() => alive && setError("Could not load this page."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  // SEO: title + meta description from the Tags table.
  useEffect(() => {
    if (!tag) return;
    const prevTitle = document.title;
    document.title = tag.seo_title || tag.tag_name || "OreVital";
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta && tag.meta_description) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta && tag.meta_description) meta.setAttribute("content", tag.meta_description);
    return () => {
      document.title = prevTitle;
      if (meta) {
        if (created) meta.remove();
        else if (prevDesc != null) meta.setAttribute("content", prevDesc);
      }
    };
  }, [tag]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f7f4]" data-testid="tag-page">
      <PublicHeader nextPath={`/tags/${slug}?d=${dealer}`} />

      <TagsMenuBar activeSlug={slug} />

      <main className="flex-1">
        {loading ? (
          <div className="py-24 text-center text-zinc-500">
            <Loader2 className="animate-spin inline mr-2" size={18} /> Loading…
          </div>
        ) : error ? (
          <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="tag-error">
            <p className="text-zinc-600">{error}</p>
            <Link to={`/d/${dealer}?view=shop`} className="text-emerald-700 font-semibold hover:underline mt-3 inline-block">
              ← Back to shop
            </Link>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
            {/* Left column — contained content card(s) */}
            <div className="space-y-5 min-w-0">
              <Link
                to={`/d/${dealer}?view=shop`}
                className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline"
                data-testid="tag-back-shop"
              >
                ‹ Back to shop
              </Link>

              {tag.html ? (
                <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
                  <HtmlFrame html={tag.html} title={tag.tag_name} />
                </div>
              ) : (
                <section className="relative overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 shadow-sm">
                  <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-200/30 blur-3xl" />
                  <div className="relative px-6 sm:px-10 py-12 sm:py-16">
                    <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700/80 mb-3">
                      OreVital · Topic
                    </span>
                    <h1 className="text-3xl sm:text-4xl font-bold text-emerald-950 tracking-tight">
                      {tag.tag_name}
                    </h1>
                    {tag.meta_description && (
                      <p className="text-base text-zinc-600 mt-4 max-w-xl leading-relaxed">
                        {tag.meta_description}
                      </p>
                    )}
                    <Link
                      to={`/d/${dealer}?view=shop`}
                      className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                      data-testid="tag-hero-shop"
                    >
                      Shop all products →
                    </Link>
                  </div>
                </section>
              )}
            </div>

            {/* Right sidebar — sticky "shop this protocol" mini-cards */}
            <aside className="block lg:sticky lg:top-28 space-y-4">
              {tag.products && tag.products.length > 0 ? (
                <div
                  className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
                  data-testid="tag-sidebar-shop-card"
                >
                  <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white px-5 py-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-200">
                      Shop this protocol
                    </div>
                    <h3 className="text-base font-bold mt-0.5">{tag.tag_name} picks</h3>
                  </div>
                  <div className="p-3 space-y-2.5">
                    {tag.products.map((p) => (
                      <Link
                        key={p.id}
                        to={`/d/${dealer}/product/${p.slug}`}
                        className="group flex items-center gap-3 rounded-xl border border-zinc-100 bg-[#fafafa] p-2.5 hover:border-emerald-200 hover:shadow-sm transition-all"
                        data-testid={`tag-sidebar-product-${p.slug}`}
                      >
                        <div className="h-14 w-14 shrink-0 rounded-lg bg-white border border-zinc-100 flex items-center justify-center overflow-hidden">
                          {p.image_url ? (
                            <img
                              src={p.image_url}
                              alt={p.name}
                              className="h-full w-full object-contain p-1"
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          ) : (
                            <ShoppingCart size={18} className="text-zinc-300" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-zinc-900 line-clamp-2 leading-snug">
                            {p.name}
                          </div>
                          <div className="text-emerald-700 font-bold text-sm mt-0.5">
                            {fmt(p.price)}
                          </div>
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-600 group-hover:bg-emerald-700 text-white text-[11px] font-semibold px-2.5 py-1.5 transition-colors">
                          <ShoppingCart size={12} /> Buy
                        </span>
                      </Link>
                    ))}
                    <Link
                      to={`/d/${dealer}?view=shop`}
                      className="block text-center text-xs font-semibold text-emerald-700 hover:underline pt-1"
                      data-testid="tag-sidebar-viewall"
                    >
                      See all products →
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-violet-800 text-white p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-violet-200">
                    Exclusive Wellness & Peptide Innovations
                  </div>
                  <h3 className="text-xl font-bold mt-3 leading-snug">
                    Meet the Family of Performance Essentials
                  </h3>
                  <Link
                    to={`/d/${dealer}?view=shop`}
                    className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-white text-violet-700 text-sm font-bold px-4 py-2.5 hover:bg-violet-50 transition-colors"
                    data-testid="tag-sidebar-shop"
                  >
                    See all products →
                  </Link>
                </div>
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
